import { useEffect, useRef } from 'react';
import { VideoElement } from '../../types/player';
import { formatTime } from '../../utils/time-utils';

export const useTimeTracking = (
  videoElement: VideoElement,
  setCurrentTime: (time: number) => void,
  setDuration: (duration: number) => void,
  loopStart: number | null,
  loopEnd: number | null,
  loopEnabled: boolean,
  handleSeek: (time: number, isUserInitiated?: boolean) => void,
) => {
  // Track last seek operation
  const lastSeekTimeRef = useRef<number>(0);
  const isUserSeekRef = useRef<boolean>(false);

  // Handle time updates
  useEffect(() => {
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoElement.currentTime);

      // Check for loop end point
      if (
        loopEnabled &&
        loopStart !== null &&
        loopEnd !== null &&
        videoElement.currentTime >= loopEnd
      ) {
        handleSeek(loopStart, false);
      }

      // Check if seeking was automatic (not user initiated)
      if (
        Math.abs(videoElement.currentTime - lastSeekTimeRef.current) > 1 &&
        !isUserSeekRef.current
      ) {
        // This could indicate timeline interaction or programmatic seeking
      }

      // Reset user seek flag after processing
      isUserSeekRef.current = false;
    };

    const handleDurationChange = () => {
      if (!isNaN(videoElement.duration) && isFinite(videoElement.duration)) {
        setDuration(videoElement.duration);
      }
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('durationchange', handleDurationChange);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('durationchange', handleDurationChange);
    };
  }, [videoElement, loopStart, loopEnd, loopEnabled, setCurrentTime, setDuration, handleSeek]);

  // Custom seek function that tracks if the seek was user initiated
  const seekTo = (newTime: number, isUserInitiated = true) => {
    if (!videoElement) return;

    lastSeekTimeRef.current = newTime;
    isUserSeekRef.current = isUserInitiated;

    videoElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  return {
    formatTime,
    seekTo,
    lastSeekTimeRef,
    isUserSeekRef,
  };
};
